const IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const cache = new WeakMap();

export function fitImageDimensions(width, height, maxDimension) {
  const scale = Math.min(1, maxDimension / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

// Run at the API boundary so every upload path, including retries, is covered.
// Keep PNG available for the admin endpoint that accepts PNG only.
export async function prepareImageUpload(file, { maxDimension = 1600, quality = 0.78, type = 'image/webp' } = {}) {
  if (!IMAGE_TYPES.has(file?.type) || !file.size) return file;
  const key = `${maxDimension}:${quality}:${type}`;
  let entries = cache.get(file);
  if (!entries) { entries = new Map(); cache.set(file, entries); }
  if (!entries.has(key)) entries.set(key, compressImage(file, { maxDimension, quality, type }));
  return entries.get(key);
}

async function compressImage(file, { maxDimension, quality, type }) {
  let url;
  let canvas;
  try {
    url = URL.createObjectURL(file);
    const image = await new Promise((resolve, reject) => {
      const element = new Image();
      const timeout = setTimeout(() => { element.src = ''; reject(new Error('Image decode timed out')); }, 15000);
      element.onload = () => { clearTimeout(timeout); resolve(element); };
      element.onerror = () => { clearTimeout(timeout); reject(new Error('Image decode failed')); };
      element.src = url;
    });
    if (!image.naturalWidth || !image.naturalHeight) return file;
    canvas = document.createElement('canvas');
    Object.assign(canvas, fitImageDimensions(image.naturalWidth, image.naturalHeight, maxDimension));
    const context = canvas.getContext('2d');
    if (!context) return file;
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise(resolve => canvas.toBlob(resolve, type, quality));
    // Unsupported output formats can silently fall back to PNG.
    if (!blob?.size || blob.type !== type || blob.size >= file.size) return file;
    const extension = type === 'image/webp' ? 'webp' : 'png';
    const result = new File([blob], `${(file.name || 'image').replace(/\.[^.]+$/, '')}.${extension}`, {
      type: blob.type, lastModified: file.lastModified ?? Date.now(),
    });
    // Selection-time compression should not be repeated just before upload.
    cache.set(result, new Map([[`${maxDimension}:${quality}:${type}`, Promise.resolve(result)]]));
    return result;
  } catch {
    // Let existing upload validation handle files the browser cannot decode.
    return file;
  } finally {
    if (url) URL.revokeObjectURL(url);
    if (canvas) { canvas.width = 0; canvas.height = 0; }
  }
}
